import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getAvailableContexts, getDefaultContext } from '../utils';
import useFetchIndustryName from 'messaging-publishing-lib/Insights/hooks/useFetchIndustryName';
import { BENCHMARK_INSIGHTS_GATE } from 'messaging-publishing-lib/constant/gates';
import { useIsUngatedFor } from 'messaging-publishing-lib/hooks/useIsUngatedFor';
import { jsx as _jsx } from "react/jsx-runtime";
export const INSIGHT_CONTEXT_QUERY_PARAM = 'insightContext';
const InsightContextContext = /*#__PURE__*/createContext(undefined);
export const InsightContextConsumer = InsightContextContext.Consumer;
/**
 * Intentended usage is to create a wrapper in the consuming app
 * which can call hooks, read state, etc. and pass a value for
 * non-children props of InsightContextProviderBase, in order
 * to create an InsightContextProvider.
 */
export const InsightContextProviderBase = ({
  shouldUseBusinessUnitContext,
  hasCampaign,
  isAutomatedEmail,
  isUngatedForGARelease,
  children
}) => {
  const initialValue = getDefaultContext({
    shouldUseBusinessUnitContext,
    hasCampaign,
    isAutomatedEmail
  });
  const [context, setContext] = useState(initialValue);
  const isDefaultValue = context === initialValue;
  const resetContext = useCallback(() => void setContext(initialValue), [initialValue]);
  const {
    industryName
  } = useFetchIndustryName();
  const isUngatedForBenchmarkInsight = useIsUngatedFor(BENCHMARK_INSIGHTS_GATE);
  const availableContexts = useMemo(() => {
    return getAvailableContexts({
      shouldUseBusinessUnitContext,
      hasCampaign,
      isAutomatedEmail,
      industryName,
      isUngatedForGARelease,
      isUngatedForBenchmarkInsight
    });
  }, [shouldUseBusinessUnitContext, hasCampaign, isAutomatedEmail, industryName, isUngatedForGARelease, isUngatedForBenchmarkInsight]);
  useEffect(() => {
    const newInitialValue = getDefaultContext({
      shouldUseBusinessUnitContext,
      hasCampaign,
      isAutomatedEmail
    });
    setContext(newInitialValue);
  }, [shouldUseBusinessUnitContext, hasCampaign, isAutomatedEmail]);
  const value = {
    availableContexts,
    context,
    isDefaultValue,
    setContext,
    resetContext,
    industryName
  };
  return /*#__PURE__*/_jsx(InsightContextContext.Provider, {
    value: value,
    children: children
  });
};
export const useInsightContext = () => {
  const context = useContext(InsightContextContext);
  if (context === undefined) {
    throw new Error('useInsightContext must be used within a InsightContextProviderBase');
  }
  return context;
};